import type { ReactElement, ReactNode } from 'react'
import { isEmpty, isNotEmpty } from '@terros/common'
import { Link } from '@tanstack/react-router'
import { Badge, Box, Divider, Group, HoverCard, Stack, Text } from '@mantine/core'
import { type MaterialSymbolType, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { ButtonCopy, TButton } from '../buttons'
import { AvatarBlurhash } from '../AvatarBlurhash'
import { type EntityAvatarProps } from './types'

export type EntityMetadataRow = {
  icon: MaterialSymbolType
  label: string
  value?: string
  copyable?: boolean
}

type Props = EntityAvatarProps & {
  subtitle?: string
  href: string
  metadata?: EntityMetadataRow[]
  color?: string
  /** Hover target is a span, for use within a line of text */
  inline?: boolean
  children: ReactNode
}

export const EntityHoverCard = ({
  children,
  name,
  subtitle,
  href,
  metadata,
  initials,
  avatarUrl,
  avatarBlurhash,
  color,
  isDeleted,
  inline,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const validMetadata = metadata?.filter((row) => isNotEmpty(row.value))
  return (
    <HoverCard width={280} shadow='md' withArrow openDelay={300} closeDelay={300} position='top-start'>
      <HoverCard.Target>
        <Box component={inline ? 'span' : 'div'}>{children}</Box>
      </HoverCard.Target>
      <HoverCard.Dropdown onClick={(e) => e.stopPropagation()}>
        <Stack gap='sm'>
          <Group gap='sm' wrap='nowrap'>
            <AvatarBlurhash
              avatarUrl={avatarUrl}
              avatarBlurhash={avatarBlurhash}
              initials={initials}
              color={color}
              size='md'
            />
            <Stack gap={0}>
              {subtitle && (
                <Text size='xs' c='dimmed' lineClamp={1}>
                  {subtitle}
                </Text>
              )}
              <Text fw={600} fs={isDeleted ? 'italic' : undefined} c={isDeleted ? 'dimmed' : undefined}>
                {name}
              </Text>
            </Stack>
          </Group>
          {isDeleted && (
            <Badge tt='none' color='red'>
              {t('In Trash')}
            </Badge>
          )}

          {isNotEmpty(validMetadata) && (
            <Stack gap='xs'>
              {validMetadata.map((row) => (
                <MetadataItem key={row.label} {...row} />
              ))}
            </Stack>
          )}

          <Divider />

          <TButton
            component={Link}
            to={href}
            target='_blank'
            rel='noopener noreferrer'
            variant='text'
            icon='open_in_new'
          >
            {t('Open in new tab')}
          </TButton>
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

const MetadataItem = ({ icon, label, value, copyable = false }: EntityMetadataRow): ReactNode => {
  if (isEmpty(value)) return
  return (
    <Group gap='xs' wrap='nowrap'>
      <MaterialSymbolIcon icon={icon} size='lg' color='var(--mantine-color-dimmed)' />
      <Text size='sm' c='dimmed' lineClamp={1}>
        {value}
      </Text>
      {copyable && <ButtonCopy value={value} variant='text' tooltip={`Copy ${label}`} />}
    </Group>
  )
}
